import React from 'react';
import {StyleSheet, View} from 'react-native';

import theme from '../style/theme';

import Text from './themed/Text';
import {Icon} from './Icon';

const PRIMARY = theme.colour.text.heading?.primary ?? '#0054A6';
const MUTED = '#9CA3AF';
const CHIP_BG = '#F3F4F6';
const CHIP_BG_DISABLED = '#F9FAFB';
const CHIP_VALUE_BG = 'rgba(0,0,0,0.04)';

export type GiftCardStoreCapabilityChipsProps = {
  canPickup: boolean;
  canDeliver: boolean;
};

/**
 * Pick up / Delivery info chips for the gift-card store locator.
 * Visual pattern mirrors StoreNameCard (icon + label + check when on),
 * but locator-neutral greys — not cart primaryDark theming.
 *
 * DIS-1460: canPickup/canDeliver mirror HomePage StoreNameCard —
 * pickup_available + delivery_available from the /members/search row.
 */
const CapabilityChip = ({
  label,
  icon,
  enabled,
  testID,
}: {
  label: string;
  icon: string;
  enabled: boolean;
  testID: string;
}) => (
  <View
    style={[styles.chip, !enabled && styles.chipDisabled]}
    testID={testID}
    accessibilityLabel={`${label}${enabled ? ' available' : ' unavailable'}`}>
    <View style={styles.chipLabel}>
      <Icon name={icon} size={11} color={enabled ? PRIMARY : MUTED} />
      <Text style={enabled ? styles.chipText : styles.chipTextDisabled}>{label}</Text>
    </View>
    {enabled ? (
      <View style={styles.chipValue}>
        <Icon name="check" size={11} color={PRIMARY} />
      </View>
    ) : null}
  </View>
);

const GiftCardStoreCapabilityChips = ({
  canPickup,
  canDeliver,
}: GiftCardStoreCapabilityChipsProps) => (
  <View style={styles.row} testID="gift-card-store-capability-chips">
    <CapabilityChip
      label="Pick up"
      icon="bag-shopping"
      enabled={canPickup}
      testID="gift-card-store-chip-pickup"
    />
    <CapabilityChip
      label="Delivery"
      icon="truck"
      enabled={canDeliver}
      testID="gift-card-store-chip-delivery"
    />
  </View>
);

export default GiftCardStoreCapabilityChips;

/** Desktop column width for the capability chips block (two chips + gap). */
export const CAPABILITY_CHIPS_COL_WIDTH = 200;

const styles = StyleSheet.create({
  // Compact neighbor chips (StoreNameCard parity): one row, content-sized —
  // never flex-grow to 50/50 on tablet/wide-mobile, never wrap to two rows.
  row: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 8,
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'stretch',
    flexGrow: 0,
    flexShrink: 1,
    backgroundColor: CHIP_BG,
    borderRadius: 4,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  chipDisabled: {
    backgroundColor: CHIP_BG_DISABLED,
    borderColor: '#F3F4F6',
  },
  chipLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  chipValue: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    backgroundColor: CHIP_VALUE_BG,
    flexShrink: 0,
  },
  chipText: {
    fontSize: 11,
    fontWeight: '600',
    color: PRIMARY,
  },
  chipTextDisabled: {
    fontSize: 11,
    fontWeight: '600',
    color: MUTED,
  },
});
